"use client"

/**
 * FAQWidget
 * Client Component for floating FAQ dialog
 * Provides quick access to FAQs without scrolling
 */

import { useState, useEffect } from "react"
import { HelpCircle, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { KnowledgeBlock } from "@/types/geo-content"

interface FAQWidgetProps {
  knowledgeBlock: KnowledgeBlock
}

export default function FAQWidget({ knowledgeBlock }: FAQWidgetProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFAQ, setSelectedFAQ] = useState<string>("faq-0")
  const [isMounted, setIsMounted] = useState(false)

  // Hydration safety - only render widget client-side
  useEffect(() => {
    setIsMounted(true)

    // Load persisted FAQ selection from localStorage
    try {
      const stored = localStorage.getItem(`faq-widget-${knowledgeBlock.serviceSlug}`)
      if (stored) {
        setSelectedFAQ(stored)
      }
    } catch {
      // localStorage not available, continue with default
    }
  }, [knowledgeBlock.serviceSlug])

  const handleFAQChange = (value: string) => {
    setSelectedFAQ(value)
    try {
      localStorage.setItem(`faq-widget-${knowledgeBlock.serviceSlug}`, value)
    } catch {
      // localStorage not available, continue without persistence
    }
  }

  if (!isMounted) {
    return null
  }

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={() => setIsOpen(true)}
        size="lg"
        className="fixed bottom-6 right-4 rounded-full shadow-lg hover:shadow-xl z-[51] gap-2"
        aria-label="FAQ Widget öffnen"
      >
        <HelpCircle className="w-5 h-5" />
        <span className="hidden sm:inline">FAQ</span>
      </Button>

      {/* FAQ Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{knowledgeBlock.serviceName} – Häufig gestellte Fragen</DialogTitle>
          </DialogHeader>

          <Accordion type="single" collapsible value={selectedFAQ} onValueChange={handleFAQChange}>
            {knowledgeBlock.faqs.map((faq, index) => (
              <AccordionItem key={`widget-faq-${index}`} value={`faq-${index}`}>
                <AccordionTrigger className="text-left hover:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Haben Sie weitere Fragen? Kontaktieren Sie mich unter{" "}
              <a href="/#contact" className="font-semibold text-primary hover:underline">
                Kontakt
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
